import styled, { css } from "styled-components"
import { DefaultTheme } from "styled-components/dist/types"

// should stay exported even do it's not used anywhere else due an error with noUnusedLocal typescript configuration
// see -> https://github.com/microsoft/TypeScript/issues/5711
export enum FontFamily {
  Inter = "'Inter', sans-serif",
  Aeonik = "'Aeonik', sans-serif",
}

export type TypographyVariant =
  | "title-1-bold"
  | "title-2-bold"
  | "title-3-semibold"
  | "title-3-inter-bold"
  | "title-4-semibold"
  | "body-1-medium"
  | "body-2-bold"
  | "body-2-semibold"
  | "body-3-semibold"
  | "body-3-medium"
  | "body-3-regular"
  | "body-4-semibold"
  | "body-4-medium"
  | "body-4-regular"
  | "body-4-link"
  | "caption-1"
  | "caption-1-bold"
  | "caption-2"
  | "caption-2-link"
  | "label-1"
  | "aeonik-extra-regular"
  | "aeonik-h1-bold"
  | "aeonik-h2-bold"
  | "aeonik-h3-bold"
  | "aeonik-h4-bold"

export const TypographyVariantsTheme: Record<
  TypographyVariant,
  {
    fontSize: string
    lineHeight: string
    fontWeight: number
    fontFamily: FontFamily
    fontStyle: string
    decoration?: string
  }
> = {
  "title-1-bold": {
    fontSize: "2rem",
    lineHeight: "3rem",
    fontWeight: 700,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "title-2-bold": {
    fontSize: "1.75rem",
    lineHeight: "2.625rem",
    fontWeight: 700,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "title-4-semibold": {
    fontSize: "1.25rem",
    lineHeight: "1.875rem",
    fontWeight: 600,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "title-3-semibold": {
    fontSize: "1.5rem",
    lineHeight: "2.25rem",
    fontWeight: 600,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "title-3-inter-bold": {
    fontSize: "1.5rem",
    lineHeight: "2.25rem",
    fontWeight: 700,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-1-medium": {
    fontSize: "1.25rem",
    lineHeight: "1.875rem",
    fontWeight: 500,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-2-bold": {
    fontSize: "1.125rem",
    lineHeight: "1.688rem",
    fontWeight: 700,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-2-semibold": {
    fontSize: "1.125rem",
    lineHeight: "1.688rem",
    fontWeight: 600,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-3-semibold": {
    fontSize: "1rem",
    lineHeight: "1.5rem",
    fontWeight: 600,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-3-medium": {
    fontSize: "1rem",
    lineHeight: "1.5rem",
    fontWeight: 500,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-3-regular": {
    fontSize: "1rem",
    lineHeight: "1.5rem",
    fontWeight: 400,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-4-semibold": {
    fontSize: "0.875rem",
    lineHeight: "1.5rem",
    fontWeight: 600,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-4-medium": {
    fontSize: "0.875rem",
    lineHeight: "1.5rem",
    fontWeight: 500,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-4-regular": {
    fontSize: "0.875rem",
    lineHeight: "1.5rem",
    fontWeight: 400,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "body-4-link": {
    fontSize: "0.875rem",
    lineHeight: "1.5rem",
    fontWeight: 500,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
    decoration: "underline",
  },
  "caption-1": {
    fontSize: "0.75rem",
    lineHeight: "1.125rem",
    fontWeight: 600,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "caption-1-bold": {
    fontSize: "0.75rem",
    lineHeight: "1.125rem",
    fontWeight: 900,
    fontFamily: FontFamily.Inter,
    fontStyle: "italic",
  },
  "caption-2": {
    fontSize: "0.75rem",
    lineHeight: "1.125rem",
    fontWeight: 400,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "caption-2-link": {
    fontSize: "0.75rem",
    lineHeight: "1.125rem",
    fontWeight: 400,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
    decoration: "underline",
  },
  "label-1": {
    fontSize: "0.75rem",
    lineHeight: "1.125rem",
    fontWeight: 600,
    fontFamily: FontFamily.Inter,
    fontStyle: "normal",
  },
  "aeonik-extra-regular": {
    fontSize: "3.75rem",
    lineHeight: "2.5rem",
    fontWeight: 400,
    fontFamily: FontFamily.Aeonik,
    fontStyle: "normal",
  },
  "aeonik-h1-bold": {
    fontSize: "2.5rem",
    lineHeight: "2.5rem",
    fontWeight: 700,
    fontFamily: FontFamily.Aeonik,
    fontStyle: "normal",
  },
  "aeonik-h2-bold": {
    fontSize: "1.5rem",
    lineHeight: "2.5rem",
    fontWeight: 700,
    fontFamily: FontFamily.Aeonik,
    fontStyle: "normal",
  },
  "aeonik-h3-bold": {
    fontSize: "1.125rem",
    lineHeight: "1.75rem",
    fontWeight: 700,
    fontFamily: FontFamily.Aeonik,
    fontStyle: "normal",
  },
  "aeonik-h4-bold": {
    fontSize: "1rem",
    lineHeight: "1.2rem",
    fontWeight: 700,
    fontFamily: FontFamily.Aeonik,
    fontStyle: "normal",
  },
}

export const getColorFromTheme = (color: TypographyProps["$color"], colors: DefaultTheme["colors"]): string => {
  if (!color) return colors.grey.$600

  const splittedcolor = (color as string).split(".") as
    | [NotNestedColors]
    | [Exclude<keyof DefaultTheme["colors"], NotNestedColors>, string]

  if (splittedcolor.length === 1) {
    return colors[splittedcolor[0]] ?? colors.grey.$600
  }

  return splittedcolor[0]
    ? colors[splittedcolor[0]][splittedcolor[1] as keyof (typeof colors)[(typeof splittedcolor)[0]]] ?? colors.grey.$600
    : colors.grey.$600
}

export const getCssFontStyles = ($variant: TypographyVariant) => {
  return css`
    font-family: ${({ theme }) => theme.typography[$variant].fontFamily};
    font-size: ${({ theme }) => theme.typography[$variant].fontSize};
    line-height: ${({ theme }) => theme.typography[$variant].lineHeight};
    font-weight: ${({ theme }) => theme.typography[$variant].fontWeight};
    text-decoration: ${({ theme }) => theme.typography[$variant].decoration ?? "none"};
  `
}

export type TypographyProps = {
  $variant?: TypographyVariant
  $color?: ThemeColors
  strikeThrough?: boolean
}

export type ThemeColors = {
  [Key in keyof DefaultTheme["colors"] & (string | number)]: DefaultTheme["colors"][Key] extends object
    ? `${Key}.${keyof DefaultTheme["colors"][Key] & (string | number)}`
    : Key
}[keyof DefaultTheme["colors"] & (string | number)]

type NotNestedColors = {
  [Key in keyof DefaultTheme["colors"] & (string | number)]: DefaultTheme["colors"][Key] extends object ? never : Key
}[keyof DefaultTheme["colors"] & (string | number)]

export const Typography = styled.p<TypographyProps>`
  ${({ $variant }) => getCssFontStyles($variant ?? "body-4-regular")}

  ${({ $color, theme }) =>
    $color ? `color: ${getColorFromTheme($color, theme.colors)};` : `color: ${theme.colors.grey.$600};`}

  ${({ strikeThrough }) => strikeThrough && `text-decoration: line-through;`}
`

Typography.displayName = "Typography"
